import { withoutRenderableSuggestions } from '@orn/global/assistant/inlineMarkup.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { FileIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import sanitizeFilename from '@orn/shared-web/utils/sanitizeFilename';
import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { splitRecapLead } from '../../lib/assistant/alertRecap';
import remarkAssistantMarkup, { ENTITY_REF_TAG, SUGGESTION_TAG } from '../../lib/assistant/remarkAssistantMarkup';

import AssistantSummaryBubble from './AssistantSummaryBubble';
import EntityChip from './EntityChip';
import SuggestionChip from './SuggestionChip';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { EntityRefType } from '../../lib/assistant/remarkAssistantMarkup';
import type { TranscriptEntities } from '../../panes/assistant/useTranscriptEntities';
import type { EntityTarget } from '../../utils/entityDestination';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';
import type { Components } from 'react-markdown';

interface IAssistantMessageProps {
  message: IConversationMessage;
  /**
   * Rows the reply references inline, resolved by the pane. Absent renders
   * every chip in its unresolved state, which is what a still-loading lookup
   * shows too.
   */
  entities?: TranscriptEntities;
  /**
   * Send a suggested next action as an ordinary user turn. Absent leaves the
   * pills inert — see {@link SuggestionChip}. The second argument echoes this
   * message's `sourceAlertId`, so the turn a tap starts can be grounded in the
   * alert the suggestion was offered on.
   */
  onSuggestionSelect?: (text: string, sourceAlertId?: string) => void;
  /**
   * The alert this reply expanded, when it is one an alert card claims —
   * threaded back through {@link onSuggestionSelect} on every suggestion tap.
   * A scalar rather than a per-message callback so the memo below keeps
   * skipping untouched bubbles during streaming.
   */
  sourceAlertId?: string;
  /**
   * Open a row an inline reference names. Absent leaves the chips inert — see
   * {@link EntityChip}.
   */
  onEntitySelect?: (target: EntityTarget) => void;
  /**
   * Render this reply as a recap of work already done — the treatment a
   * replayed alert summary takes when its alert is not new to the user. The
   * opening paragraph moves into {@link AssistantSummaryBubble}; the ask and
   * its chips stay live prose below it. Assistant turns only.
   */
  isRecap?: boolean;
  /**
   * The user has taken one of this reply's suggestions, so every suggestion
   * button on it comes down — an invitation that has been answered. Inline
   * entity chips are untouched. Assistant turns only.
   */
  suggestionsSpent?: boolean;
}

/**
 * The reply as it should read now. Taking a suggestion retires every suggestion
 * button on the row, so those tokens come out of the text rather than rendering
 * as empty nodes where the buttons were.
 *
 * `withoutRenderableSuggestions` removes `<suggestion=…>` and nothing else, so
 * the inline entity refs stay and their chips draw as before.
 *
 * A reply that was nothing but suggestions keeps them: stripping it would leave
 * a row with nothing in it, and the transcript still counts the row renderable.
 */
function visibleContent(content: string, suggestionsSpent: boolean): string {
  if (!suggestionsSpent) {
    return content;
  }
  const stripped = withoutRenderableSuggestions(content).trimEnd();
  return stripped.trim().length > 0 ? stripped : content;
}

const ATTACHED_DOC_FALLBACK = 'Attached document';

/**
 * The renderers for the elements `remarkAssistantMarkup` emits.
 *
 * Cast because `react-markdown`'s `Components` is keyed by the HTML element
 * names in `JSX.IntrinsicElements`, and these are ours — the alternative is
 * augmenting the global JSX namespace to describe tags that exist for exactly
 * one renderer. Kept to these entries so the rest of the map stays checked.
 */
const assistantMarkupComponents = (
  entities: TranscriptEntities | undefined,
  onSuggestionSelect: ((text: string) => void) | undefined,
  onEntitySelect: ((target: EntityTarget) => void) | undefined
): Partial<Components> =>
  ({
    [ENTITY_REF_TAG]: ({ refType, refId }: { refType?: string; refId?: string }) => {
      const type = (refType ?? 'merchant') as EntityRefType;
      return <EntityChip refType={type} target={entities?.[type]?.[refId ?? ''] ?? null} onSelect={onEntitySelect} />;
    },
    [SUGGESTION_TAG]: ({ suggestionText }: { suggestionText?: string }) =>
      suggestionText === undefined || suggestionText.trim() === '' ? null : (
        <SuggestionChip text={suggestionText} onSelect={onSuggestionSelect} />
      ),
  }) as Partial<Components>;

interface IAssistantMarkdownProps {
  content: string;
  isStreaming: boolean;
  entities: TranscriptEntities | undefined;
  onSuggestionSelect: ((text: string) => void) | undefined;
  onEntitySelect: ((target: EntityTarget) => void) | undefined;
}

/**
 * The assistant's own markdown, with the inline-markup renderers wired in.
 *
 * Its own component because a replayed alert summary renders in two pieces —
 * the recap inside a bubble, the ask outside it — and both halves have to go
 * through the identical pipeline or an entity chip would resolve on one side
 * and not the other.
 */
const AssistantMarkdown = ({
  content,
  isStreaming,
  entities,
  onSuggestionSelect,
  onEntitySelect,
}: IAssistantMarkdownProps) => (
  <Markdown
    // A token still arriving is hidden until it closes, so a
    // half-written `<merchant_id=8f14` never flickers on screen as
    // prose. Only while streaming — a finalized message keeps its
    // text exactly as the model wrote it.
    remarkPlugins={[
      remarkGfm,
      [
        remarkAssistantMarkup,
        {
          suppressTrailingPartial: isStreaming,
          // Rebuilt per render, so a replayed summary's `<date=… />` reads
          // against the viewer's clock now, not the day it was stored.
          dateMacroContext: { now: new Date(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        },
      ],
    ]}
    components={{
      ...assistantMarkupComponents(entities, onSuggestionSelect, onEntitySelect),
      h2: ({ children }) => <div className="text-primary mb-2 mt-3 text-base font-semibold first:mt-0">{children}</div>,
      h3: ({ children }) => <div className="text-primary mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>,
      p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
      strong: ({ children }) => <span className="font-semibold">{children}</span>,
      ul: ({ children }) => <ul className="mb-2 list-outside list-disc pl-5">{children}</ul>,
      ol: ({ children }) => <ol className="mb-2 list-outside list-decimal pl-5">{children}</ol>,
      li: ({ children }) => <li className="mb-0.5 pl-1">{children}</li>,
      hr: () => <hr className="border-primary my-3" />,
      table: ({ children }) => (
        <div className="my-2 overflow-x-auto">
          <table className="border-primary bg-primary w-full border-collapse border text-xs">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-tertiary">{children}</thead>,
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => <tr className="border-primary border-b last:border-b-0">{children}</tr>,
      th: ({ children }) => (
        <th className="border-primary text-primary border-r px-2 py-1 text-left font-semibold last:border-r-0">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="border-primary text-primary border-r px-2 py-1 align-top last:border-r-0">{children}</td>
      ),
      // Fenced code blocks: the <pre> wrapper owns background,
      // padding, and — critically — line wrapping, so a long line
      // (e.g. the `/context` system-prompt dump) wraps inside the
      // bubble instead of overflowing it.
      pre: ({ children }) => (
        <pre className="bg-tertiary my-2 overflow-x-auto whitespace-pre-wrap break-words rounded p-2 font-mono text-xs">
          {children}
        </pre>
      ),
      code: ({ className, children }) =>
        className?.includes('language-') ? (
          // Block code lives inside the styled <pre> above — keep it
          // bare so it doesn't double the background / padding.
          <code className={className}>{children}</code>
        ) : (
          <code className="bg-tertiary rounded px-1 py-0.5 font-mono text-xs">{children}</code>
        ),
    }}
  >
    {content}
  </Markdown>
);

/**
 * Renders a single message with appropriate styling for user vs assistant.
 * User turns keep a right-aligned bubble; assistant turns render bubble-less
 * markdown that runs the full width of the conversation column.
 *
 * User turns may carry an `attachments` array — rendered as a chip row
 * above the content bubble, filename + file icon per chip. v1 stops at
 * filename / icon; thumbnails + lightbox are post-MVP. Server-supplied
 * filenames are truncated and stripped of control chars before render so
 * a 500-char or escape-laden filename can't break layout.
 *
 * Under `isRecap` an assistant turn splits in two: the opening paragraph is the
 * assistant recapping work it already did and renders inside
 * {@link AssistantSummaryBubble}, and everything after it stays ordinary prose
 * so the closing ask and its suggestion chips still read as live.
 */
const AssistantMessage = ({
  message,
  entities,
  onSuggestionSelect,
  onEntitySelect,
  isRecap = false,
  suggestionsSpent = false,
  sourceAlertId,
}: IAssistantMessageProps) => {
  // Narrows the chip-facing callback back to `(text)` while binding this
  // message's alert. Rebuilt only when this bubble re-renders, which the memo
  // below already limits to prop changes.
  const handleSuggestionSelect = React.useMemo(() => {
    if (onSuggestionSelect === undefined) {
      return undefined;
    }
    return sourceAlertId === undefined
      ? (text: string) => onSuggestionSelect(text)
      : (text: string) => onSuggestionSelect(text, sourceAlertId);
  }, [onSuggestionSelect, sourceAlertId]);
  const isUser = message.role === 'user';
  const content = isUser ? message.content : visibleContent(message.content, suggestionsSpent);
  const recap = isRecap && !isUser ? splitRecapLead(content) : null;
  // Pending user bubbles (not yet confirmed by the server) render
  // semi-transparent so they read as "not landed yet": a queued bubble (typed
  // mid-turn, or held while the tab is superseded) fades most; an in-flight
  // bubble the server is actively processing fades less. The flag clears on
  // confirmation and the bubble fades up to full. Assistant bubbles never carry
  // these flags.
  const isPendingUser = isUser && message.isPending === true;
  const isQueued = isPendingUser && message.isInFlight === false;
  const pendingOpacity = isPendingUser ? (isQueued ? 'opacity-50' : 'opacity-80') : '';
  // An attachment-only user message carries no text — skip its bubble so the
  // file chips don't trail an empty blob. Assistant bubbles always render
  // (they may be mid-stream with no content yet).
  const showBubble = !isUser || content.trim().length > 0;

  return (
    <div
      className={cx('flex w-full flex-col transition-opacity', isUser ? 'items-end' : 'items-start', pendingOpacity)}
    >
      {isUser && message.attachments && message.attachments.length > 0 && (
        <UserAttachmentsRow attachments={message.attachments} />
      )}
      {showBubble && (
        <div
          // User turns keep a right-aligned bubble; assistant turns drop the
          // bubble chrome and run full-width as plain prose on the pane surface.
          className={cx(isUser ? 'bg-primary text-primary max-w-[85%] rounded-2xl px-4 py-3' : 'w-full')}
        >
          {isUser ? (
            <div className="text-reg whitespace-pre-wrap break-words leading-relaxed">{content}</div>
          ) : (
            <div className="text-primary text-reg leading-relaxed">
              {recap === null ? (
                <AssistantMarkdown
                  content={content}
                  isStreaming={message.isStreaming === true}
                  entities={entities}
                  onSuggestionSelect={handleSuggestionSelect}
                  onEntitySelect={onEntitySelect}
                />
              ) : (
                <>
                  <AssistantSummaryBubble>
                    <AssistantMarkdown
                      content={recap.recap}
                      isStreaming={message.isStreaming === true}
                      entities={entities}
                      onSuggestionSelect={handleSuggestionSelect}
                      onEntitySelect={onEntitySelect}
                    />
                  </AssistantSummaryBubble>
                  {recap.rest !== '' && (
                    <div className="mt-3">
                      <AssistantMarkdown
                        content={recap.rest}
                        isStreaming={message.isStreaming === true}
                        entities={entities}
                        onSuggestionSelect={handleSuggestionSelect}
                        onEntitySelect={onEntitySelect}
                      />
                    </div>
                  )}
                </>
              )}
              {/* While this round's text streams, the "working" affordance sits on
                its own line at the bottom of the growing message. On finalize the
                flag clears and the spinner pops back out on its own (the pane's
                standalone spinner) until the turn ends. */}
              {message.isStreaming === true && (
                // `w-fit` shrinks the indicator's own centering box to the spinner
                // so it sits at the left edge of the text column, not centered
                // across it.
                <LoadingIndicator type="line-spinner" size="xs" className="mt-2 w-fit" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface IUserAttachmentsRowProps {
  attachments: IObservationDocument[];
}

const UserAttachmentsRow = ({ attachments }: IUserAttachmentsRowProps) => (
  <div className="mb-1 flex max-w-[85%] flex-wrap justify-end gap-1.5">
    {attachments.map((doc) => (
      <div
        key={doc.id}
        className="bg-secondary border-primary text-primary flex max-w-xs items-center gap-1.5 rounded-lg border px-2 py-1 text-xs"
        title={doc.name ?? ATTACHED_DOC_FALLBACK}
      >
        <FileIcon className="text-tertiary size-3.5 shrink-0" />
        <span className="truncate">{sanitizeFilename(doc.name)}</span>
      </div>
    ))}
  </div>
);

// Memoized: token-level streaming re-creates the messages array on every delta,
// but only the growing bubble's `message` object changes identity. Shallow-prop
// memo lets every untouched bubble skip its (markdown) re-render.
export default React.memo(AssistantMessage);
